import { useEffect, useState } from "preact/hooks";
import type { ResolvedLayout } from "../../sdk";
import { appearanceFor } from "../../shared/appearance";
import { checkThemeDesign } from "../../shared/theme-design";
import { checkLayout, checkSvg, checkTokens, resolveLayout } from "../../shared/themes";
import type { CustomTheme, Settings } from "../../shared/types";
import { LayoutFields } from "../components/LayoutFields";
import { Field, Modal } from "../components/ui";
import {
  applyTheme,
  customTokens,
  customVariants,
  findTheme,
  installedThemes,
  setCustomTheme,
} from "../settings-shared";
import { app, errorToast } from "../store";

/** The colors the editor offers, in the order people tend to think about them. */
const COLOURS: [string, string][] = [
  ["bg", "Page background"],
  ["surface", "Panels"],
  ["surface-2", "Raised and hovered areas"],
  ["surface-3", "Pressed areas"],
  ["border", "Lines"],
  ["border-strong", "Strong lines"],
  ["text", "Text"],
  ["text-2", "Secondary text"],
  ["text-3", "Muted text"],
  ["accent", "Accent"],
  ["accent-hover", "Accent when hovered"],
  ["accent-soft", "Accent background"],
  ["accent-text", "Text on the accent"],
  ["row-active", "Open message in the list"],
  ["row-checked", "Selected messages"],
  ["unread-bg", "Unread messages"],
  ["read-bg", "Read messages"],
  ["danger", "Danger"],
  ["danger-soft", "Danger background"],
  ["warning", "Warning"],
  ["warning-soft", "Warning background"],
  ["success", "Success"],
  ["success-soft", "Success background"],
  ["flag", "Flags and stars"],
  ["toast-bg", "Notices at the bottom"],
  ["message-bg", "Behind email content"],
];

/** The tokens beyond colour, by group; any CSS value of the right kind. */
const TYPOGRAPHY: [string, string][] = [
  ["font", "Text font (a font-family stack)"],
  ["font-display", "Headings font"],
  ["mono", "Monospace font"],
  ["font-size", "Text size"],
  ["line-height", "Line height"],
  ["title-size", "Conversation title size"],
  ["weight-strong", "Bold weight"],
  ["weight-title", "Title weight"],
];

const SHAPE: [string, string][] = [
  ["sidebar-w", "Sidebar width"],
  ["list-w", "Message list width"],
  ["topbar-h", "Top bar height"],
  ["toolbar-h", "Toolbar height"],
  ["row-pad-y", "Space above and below a row"],
  ["row-pad-x", "Space beside a row"],
  ["radius", "Corners"],
  ["radius-sm", "Small corners"],
  ["radius-panel", "Panel corners"],
  ["border-w", "Line width"],
  ["shadow", "Card shadow"],
  ["shadow-lg", "Popover shadow"],
  ["focus-ring", "Focus ring (an outline)"],
  ["button-radius", "Button corners"],
  ["field-radius", "Input corners"],
  ["nav-radius", "Navigation corners"],
  ["control-h", "Control height"],
  ["panel-shadow", "Workspace elevation"],
  ["glass-bg", "Glass surface"],
  ["glass-blur", "Glass blur"],
  ["canvas-image", "Canvas gradient"],
];

const MOTION: [string, string][] = [
  ["duration", "Quick transitions"],
  ["duration-slow", "Slower transitions (panels, the rail)"],
  ["ease", "Easing"],
];

const GROUPS = [
  "Colours",
  "Typography",
  "Spacing and shape",
  "Layout",
  "Motion",
  "Branding",
] as const;
type Group = (typeof GROUPS)[number];

/** #abc → #aabbcc, so a color picker can show it; anything else stays as it is. */
const asHex = (value: string): string | null => {
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(value);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return /^#[0-9a-f]{6}$/i.test(value) ? value : null;
};

export const fromBase = (id: string): CustomTheme => {
  const base = findTheme(id) ?? installedThemes()[0];
  return {
    name: `My ${base?.name ?? "theme"}`,
    base: base?.id ?? "light",
    scheme: base?.scheme ?? "light",
    tokens: {},
    darkTokens: {},
    oledTokens: {},
    ...(base?.design ? { design: base.design } : {}),
  };
};

/** Reads a theme.json (from themes/, uploaded, or exported here) into a custom theme. */
export const parseThemeFile = (text: string): CustomTheme => {
  const data = JSON.parse(text) as Partial<CustomTheme> & {
    tokens?: unknown;
    layout?: unknown;
    brand?: { logo?: unknown; name?: unknown };
  };
  if (
    typeof data.name !== "string" ||
    (data.scheme !== "light" && data.scheme !== "dark") ||
    typeof data.tokens !== "object" ||
    data.tokens === null
  ) {
    throw new Error("That file isn't a Cloudmail theme (it needs a name, scheme and tokens).");
  }
  const tokens = Object.fromEntries(
    Object.entries(data.tokens as Record<string, unknown>).filter(
      (entry): entry is [string, string] =>
        /^[a-z][a-z0-9-]{0,39}$/.test(entry[0]) && typeof entry[1] === "string",
    ),
  );
  const base =
    typeof data.base === "string" && findTheme(data.base)
      ? data.base
      : (installedThemes().find((theme) => theme.scheme === data.scheme)?.id ?? "light");
  // A folder theme's font files can't come along; the rest of its layout can.
  const { fonts: _fonts, ...rawLayout } = (
    data.layout && typeof data.layout === "object" ? data.layout : {}
  ) as Record<string, unknown>;
  const layout = checkLayout(rawLayout);
  // A logo that is a file name (a folder theme's) can't come along; one that is an SVG can.
  const logo =
    typeof data.brand?.logo === "string" && data.brand.logo.trim().startsWith("<")
      ? checkSvg(data.brand.logo, "brand.logo")
      : undefined;
  const name = data.brand?.name === "hide" ? "hide" : undefined;
  return {
    name: data.name.slice(0, 60),
    base,
    scheme: data.scheme,
    tokens,
    ...(data.darkTokens ? { darkTokens: checkTokens(data.darkTokens) } : {}),
    ...(data.oledTokens ? { oledTokens: checkTokens(data.oledTokens) } : {}),
    layout,
    ...(data.design ? { design: checkThemeDesign(data.design) } : {}),
    ...(logo || name ? { brand: { ...(logo ? { logo } : {}), ...(name ? { name } : {}) } } : {}),
  };
};

/** theme.json for this theme, ready to upload in Admin → Themes (the logo is in it). */
export const download = (theme: CustomTheme) => {
  const file = {
    name: theme.name,
    base: theme.base,
    description: `Made in the Cloudmail theme editor, from ${theme.base}.`,
    scheme: theme.scheme,
    tokens: customTokens(theme),
    ...customVariants(theme),
    layout: { ...findTheme(theme.base)?.layout, ...theme.layout },
    ...(theme.brand ? { brand: theme.brand } : {}),
    ...(theme.design ? { design: theme.design } : {}),
  };
  const link = document.createElement("a");
  link.href = URL.createObjectURL(
    new Blob([`${JSON.stringify(file, null, 2)}\n`], { type: "application/json" }),
  );
  link.download = "theme.json";
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

/** A small picture of the arrangement: sidebar, list and reader, as the layout puts them. */
const LayoutPreview = ({ layout }: { layout: ResolvedLayout }) => (
  <div
    class="layout-preview"
    data-sidebar={layout.sidebar}
    data-pane={layout.readingPane}
    data-list={layout.listStyle}
    data-density={layout.density}
    aria-label="Layout preview"
    role="img"
  >
    <span class="lp-side">
      <i />
      <i />
      <i />
    </span>
    <span class="lp-main">
      <span class="lp-list">
        <i />
        <i />
        <i />
        <i />
      </span>
      {layout.readingPane !== "off" && (
        <span class="lp-reader">
          {layout.toolbar === "top" && <b class="lp-toolbar" />}
          <b class="lp-title" />
          {layout.toolbar === "inline" && <b class="lp-toolbar inline" />}
        </span>
      )}
    </span>
  </div>
);

/**
 * Makes a personal theme: pick a theme to start from, change its colors, type, shape, layout,
 * motion and logo, and see them on the whole app as you go. Save keeps it as "custom";
 * Download gives a theme.json to upload in Admin → Themes.
 */
export const ThemeEditor = ({
  settings,
  onSave,
  onClose,
}: {
  settings: Settings;
  onSave: (patch: Partial<Settings>) => Promise<void>;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState<CustomTheme>(
    () => settings.customTheme ?? fromBase(settings.theme),
  );
  const [group, setGroup] = useState<Group>("Colours");
  const [saving, setSaving] = useState(false);
  const [variant, setVariant] = useState<"light" | "dark" | "oled">(draft.scheme);

  // Shows the draft on the page as it changes, and puts the saved look back on close.
  useEffect(() => {
    setCustomTheme(draft);
    applyTheme("custom", "theme", {
      ...appearanceFor(settings.theme, settings.appearance),
      mode: variant,
      customAccent: null,
    });
  }, [draft, variant]);
  useEffect(
    () => () => {
      // Saved or not, the page goes back to what the settings now say.
      const current = app.get().me?.settings ?? settings;
      setCustomTheme(current.customTheme);
      applyTheme(current.theme, current.accent);
    },
    [],
  );

  const variants = customVariants(draft);
  const values = {
    ...customTokens(draft),
    ...(variant !== "light" ? variants.darkTokens : {}),
    ...(variant === "oled" ? variants.oledTokens : {}),
  };
  const setToken = (token: string, value: string) => {
    const key =
      variant === "oled"
        ? "oledTokens"
        : variant === "dark" && draft.scheme === "light"
          ? "darkTokens"
          : "tokens";
    const tokens = { ...draft[key] };
    if (value.trim()) tokens[token] = value.trim();
    else delete tokens[token];
    setDraft({ ...draft, [key]: tokens });
  };
  const baseLayout = findTheme(draft.base)?.layout;
  const layout = resolveLayout(baseLayout, draft.layout);
  const computed = getComputedStyle(document.documentElement);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ customTheme: draft, theme: "custom" });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const textTokens = (list: [string, string][]) => (
    <div class="theme-editor-grid">
      {list.map(([token, label]) => (
        <Field key={token} label={label}>
          <input
            class="theme-token-value"
            value={draft.tokens[token] ?? ""}
            placeholder={values[token] ?? computed.getPropertyValue(`--${token}`).trim()}
            spellcheck={false}
            onChange={(event) => setToken(token, (event.target as HTMLInputElement).value)}
          />
        </Field>
      ))}
    </div>
  );

  return (
    <Modal
      title="Theme editor"
      onClose={onClose}
      wide
      footer={
        <>
          <label class="button">
            Open a theme file
            <input
              type="file"
              accept="application/json,.json"
              hidden
              onChange={async (event) => {
                const file = (event.target as HTMLInputElement).files?.[0];
                if (!file) return;
                try {
                  setDraft(parseThemeFile(await file.text()));
                } catch (error) {
                  errorToast(error);
                }
              }}
            />
          </label>
          <button type="button" onClick={() => download(draft)}>
            Download theme.json
          </button>
          <span class="spacer" />
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="primary" disabled={saving} onClick={() => void save()}>
            Save and use
          </button>
        </>
      }
    >
      <p class="hint">
        Changes show on the whole app as you make them. The accent color you pick in Appearance
        still wins over this theme's accent, unless it is Theme accent, and your own layout choices
        win over this theme's layout.
      </p>
      <div class="theme-editor-head">
        <Field label="Name">
          <input
            value={draft.name}
            maxLength={60}
            onInput={(event) =>
              setDraft({ ...draft, name: (event.target as HTMLInputElement).value })
            }
          />
        </Field>
        <Field label="Start from">
          <select
            value={draft.base}
            onChange={(event) => {
              const base = fromBase((event.target as HTMLSelectElement).value);
              // A name nobody typed follows the theme it starts from.
              const named = draft.name !== fromBase(draft.base).name;
              setDraft({ ...base, name: named ? draft.name : base.name, tokens: {} });
            }}
          >
            {installedThemes().map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Palette to edit">
          <select
            aria-label="Palette to edit"
            value={variant}
            onChange={(event) =>
              setVariant((event.target as HTMLSelectElement).value as typeof variant)
            }
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="oled">OLED</option>
          </select>
        </Field>
      </div>
      <div class="theme-editor-tabs" role="tablist" aria-label="Parts of the theme">
        {GROUPS.map((entry) => (
          <button
            key={entry}
            type="button"
            role="tab"
            aria-selected={group === entry}
            class={`theme-editor-tab${group === entry ? " active" : ""}`}
            onClick={() => setGroup(entry)}
          >
            {entry}
          </button>
        ))}
      </div>
      {group === "Colours" && (
        <div class="theme-editor-grid">
          {COLOURS.map(([token, label]) => {
            const value = values[token] ?? "";
            const hex = asHex(value);
            return (
              <label key={token} class="theme-token">
                <input
                  type="color"
                  value={hex ?? "#000000"}
                  aria-label={label}
                  onInput={(event) => setToken(token, (event.target as HTMLInputElement).value)}
                />
                <span class="theme-token-text">
                  <span>{label}</span>
                  <input
                    class="theme-token-value"
                    value={value}
                    spellcheck={false}
                    aria-label={`${label} value`}
                    onChange={(event) => setToken(token, (event.target as HTMLInputElement).value)}
                  />
                </span>
              </label>
            );
          })}
        </div>
      )}
      {group === "Typography" && textTokens(TYPOGRAPHY)}
      {group === "Spacing and shape" && textTokens(SHAPE)}
      {group === "Layout" && (
        <div class="theme-editor-layout">
          <div>
            <LayoutFields
              value={draft.layout ?? {}}
              inherited={resolveLayout(baseLayout)}
              inheritLabel="As the theme it starts from"
              keys={["readingPane", "sidebar", "density", "listStyle", "toolbar"]}
              onChange={(next) => setDraft({ ...draft, layout: next })}
            />
          </div>
          <LayoutPreview layout={layout} />
        </div>
      )}
      {group === "Motion" && (
        <>
          <LayoutFields
            value={draft.layout ?? {}}
            inherited={resolveLayout(baseLayout)}
            inheritLabel="As the theme it starts from"
            keys={["motion"]}
            onChange={(next) => setDraft({ ...draft, layout: next })}
          />
          {textTokens(MOTION)}
        </>
      )}
      {group === "Branding" && (
        <>
          <Field label="Logo" hint="An SVG image, shown in the top bar and on the sign-in page.">
            <div class="row-actions">
              <label class="button">
                Choose an SVG
                <input
                  type="file"
                  accept="image/svg+xml,.svg"
                  hidden
                  onChange={async (event) => {
                    const file = (event.target as HTMLInputElement).files?.[0];
                    if (!file) return;
                    try {
                      const logo = checkSvg(await file.text(), file.name);
                      setDraft({ ...draft, brand: { ...draft.brand, logo } });
                    } catch (error) {
                      errorToast(error);
                    }
                  }}
                />
              </label>
              {draft.brand?.logo && (
                <button
                  type="button"
                  onClick={() => {
                    const { logo: _logo, ...brand } = draft.brand ?? {};
                    setDraft({ ...draft, brand });
                  }}
                >
                  Use the app's logo
                </button>
              )}
            </div>
          </Field>
          <Field label="App name beside the logo">
            <select
              value={draft.brand?.name ?? "show"}
              onChange={(event) => {
                const name = (event.target as HTMLSelectElement).value as "show" | "hide";
                setDraft({ ...draft, brand: { ...draft.brand, name } });
              }}
            >
              <option value="show">Show</option>
              <option value="hide">Only the logo</option>
            </select>
          </Field>
        </>
      )}
      {(Object.keys(draft.tokens).length > 0 || draft.layout || draft.brand) && (
        <button
          type="button"
          onClick={() =>
            setDraft({ name: draft.name, base: draft.base, scheme: draft.scheme, tokens: {} })
          }
        >
          Undo all changes
        </button>
      )}
    </Modal>
  );
};
